import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Plus, Search, ChevronRight, Download, Trash2, Pencil, 
  Upload, FileText, Check, X, AlertCircle, FileDown, List,
  Filter, Printer, WifiOff, RefreshCw, Square, CheckSquare, MinusSquare,
  Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { formatLBP, formatUSD, getAccountClassName, getNumberClass } from '../lib/utils';
import LedgerDialog from '../components/LedgerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChartOfAccountsPage = () => {
  const { currentOrg, canEdit, canAdmin, user } = useAuth();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const accName = (a) => {
    if (!a) return '';
    if (lang === 'fr') return a.name_fr || a.name || a.name_pt || '';
    if (lang === 'pt') return a.name_pt || a.name || a.name_fr || '';
    return a.name || a.name_fr || a.name_pt || '';
  };
  const { selectedFY } = useFiscalYear();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 200;
  const [selectedClass, setSelectedClass] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  
  // CSV/Excel Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importStep, setImportStep] = useState('upload'); // upload, preview, result
  const [importResult, setImportResult] = useState(null);
  const [importType, setImportType] = useState('csv'); // csv or excel
  const fileInputRef = useRef(null);

  // Retry/Re-update state for handling connection failures
  const [failedOperation, setFailedOperation] = useState(null); // {type: 'create'|'update', account: {...}, error: '...'}
  const [retrying, setRetrying] = useState(false);

  const [newAccount, setNewAccount] = useState({
    code: '',
    name: '',
    name_fr: '',
    name_pt: '',
    account_class: 1,
    account_type: 'asset',
    parent_code: '',
    is_group: false,
    normal_balance: 'debit'
  });

  // Filtered List state
  const [filterListOpen, setFilterListOpen] = useState(false);
  const [filterContent, setFilterContent] = useState('');
  const [filterBalance, setFilterBalance] = useState('all'); // all, non-zero, zero
  const [filterCodeLength, setFilterCodeLength] = useState('all'); // all, 2, 4, more

  // Bulk delete state
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);

  useEffect(() => {
    if (currentOrg) {
      setCurrentPage(0);
      fetchAccounts(true);
    }
  }, [currentOrg, selectedFY, searchTerm]);

  const fetchAccounts = async (reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams({ 
        organization_id: currentOrg.id,
        skip: reset ? 0 : currentPage * PAGE_SIZE,
        limit: PAGE_SIZE
      });
      if (selectedFY?.id) params.append('fy_id', selectedFY.id);
      if (searchTerm) params.append('search', searchTerm);
      const response = await axios.get(`${API}/accounts?${params.toString()}`);
      const data = response.data;
      const accts = data.accounts || data;
      const total = data.total || (Array.isArray(data) ? data.length : 0);
      
      if (reset) {
        setAccounts(Array.isArray(accts) ? accts : []);
        setCurrentPage(1);
      } else {
        setAccounts(prev => [...prev, ...(Array.isArray(accts) ? accts : [])]);
        setCurrentPage(prev => prev + 1);
      }
      setTotalCount(total);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      if (!error.response && error.message === 'Network Error') {
        alert('Connection Error: Unable to connect to the server. Please check your internet connection.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => fetchAccounts(false);
  const hasMore = accounts.length < totalCount;

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newAccount,
        organization_id: currentOrg.id
      };
      
      if (editingAccount) {
        await axios.put(`${API}/accounts/${editingAccount.id}`, payload);
      } else {
        await axios.post(`${API}/accounts`, payload);
      }
      setFailedOperation(null);
      
      setIsDialogOpen(false);
      resetForm();
      fetchAccounts(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save account';
      // Store the failed operation for retry
      setFailedOperation({
        type: editingAccount ? 'update' : 'create',
        accountId: editingAccount?.id,
        payload: {
          ...newAccount,
          organization_id: currentOrg.id
        },
        error: errorMsg
      });
      alert(`Error: ${errorMsg}. You can retry using the "Re-update" button.`);
    }
  };

  // Retry failed operation
  const handleRetryOperation = async () => {
    if (!failedOperation) return;
    
    setRetrying(true);
    try {
      if (failedOperation.type === 'update') {
        await axios.put(`${API}/accounts/${failedOperation.accountId}`, failedOperation.payload);
      } else {
        await axios.post(`${API}/accounts`, failedOperation.payload);
      }
      
      setFailedOperation(null);
      setIsDialogOpen(false);
      resetForm();
      fetchAccounts(true);
      alert('Account saved successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save account';
      setFailedOperation({
        ...failedOperation,
        error: errorMsg
      });
      alert(`Retry failed: ${errorMsg}`);
    } finally {
      setRetrying(false);
    }
  };

  // Rebuild/Update accounts from historical vouchers
  const [rebuildingFromVouchers, setRebuildingFromVouchers] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);

  const handleRebuildFromVouchers = async () => {
    if (!currentOrg) return;
    
    const confirmRebuild = window.confirm(
      'This will scan all vouchers and create/update accounts based on voucher line data.\n\n' +
      'This is useful if accounts are missing or have incorrect data.\n\n' +
      'Continue?'
    );
    
    if (!confirmRebuild) return;
    
    setRebuildingFromVouchers(true);
    setRebuildResult(null);
    
    try {
      const response = await axios.post(`${API}/accounts/rebuild-from-vouchers`, {
        organization_id: currentOrg.id
      });
      
      setRebuildResult(response.data);
      fetchAccounts(true);
      alert(`Rebuild complete!\nCreated: ${response.data.created || 0}\nUpdated: ${response.data.updated || 0}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to rebuild accounts';
      alert(`Error: ${errorMsg}`);
    } finally {
      setRebuildingFromVouchers(false);
    }
  };

  const handleSeedCOA = async () => {
    setSeeding(true);
    try {
      const hasExisting = (accounts && accounts.length > 0) || totalCount > 0;
      const url = `${API}/accounts/seed-coa?organization_id=${currentOrg.id}${hasExisting ? '&force=true' : ''}`;
      const response = await axios.post(url);
      alert(response.data.message);
      setSeedDialogOpen(false);
      fetchAccounts(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to seed COA');
    } finally {
      setSeeding(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/accounts/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      fetchAccounts(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  // Bulk delete functions
  const hasZeroBalance = (account) => {
    const lbp = Math.abs(account.balance_lbp || 0);
    const usd = Math.abs(account.balance_usd || 0);
    return lbp < 0.01 && usd < 0.01;
  };

  const selectableAccounts = accounts.filter(hasZeroBalance);

  const toggleAccountSelection = (accountId) => {
    setSelectedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAccounts.size === selectableAccounts.length && selectableAccounts.length > 0) {
      setSelectedAccounts(new Set());
    } else {
      setSelectedAccounts(new Set(selectableAccounts.map(a => a.id)));
    }
  };

  const getSelectAllState = () => {
    if (selectedAccounts.size === 0) return 'none';
    if (selectedAccounts.size === selectableAccounts.length) return 'all';
    return 'partial';
  };

  const handleBulkDelete = async () => {
    if (selectedAccounts.size === 0) return;
    
    setBulkDeleting(true);
    setBulkDeleteResult(null);
    
    try {
      const response = await axios.post(`${API}/accounts/bulk-delete`, {
        account_ids: Array.from(selectedAccounts)
      });
      
      setBulkDeleteResult(response.data);
      
      // Clear selection and refresh if any were deleted
      if (response.data.deleted_count > 0) {
        setSelectedAccounts(new Set());
        fetchAccounts(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Bulk delete failed';
      alert(`Error: ${errorMsg}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const closeBulkDeleteDialog = () => {
    setBulkDeleteDialogOpen(false);
    setBulkDeleteResult(null);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setNewAccount({
      code: account.code,
      name: account.name,
      name_fr: account.name_fr || '',
      name_pt: account.name_pt || '',
      account_class: account.account_class,
      account_type: account.account_type,
      parent_code: account.parent_code || '',
      is_group: account.is_group || false,
      normal_balance: account.normal_balance || 'debit'
    });
    setIsDialogOpen(true);
  };

  // Toggle account visibility from cashier (super_admin only)
  const handleToggleCashierVisibility = async (account) => {
    try {
      const response = await axios.put(`${API}/accounts/${account.id}/toggle-cashier-visibility`);
      // Update the account in local state
      setAccounts(prev => prev.map(acc => 
        acc.id === account.id ? { ...acc, hidden_from_cashier: response.data.hidden_from_cashier } : acc
      ));
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      alert(error.response?.data?.detail || 'Failed to toggle account visibility');
    }
  };

  const resetForm = () => {
    setNewAccount({
      code: '',
      name: '',
      name_fr: '',
      name_pt: '',
      account_class: 1,
      account_type: 'asset',
      parent_code: '',
      is_group: false,
      normal_balance: 'debit'
    });
    setEditingAccount(null);
  };

  // CSV/Excel Import handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isCSV = file.name.endsWith('.csv');
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      
      if (isCSV || isExcel) {
        setImportFile(file);
        setImportType(isExcel ? 'excel' : 'csv');
        setImportStep('upload');
        setPreviewData(null);
        setImportResult(null);
      } else {
        alert('Please select a CSV or Excel (.xlsx) file');
      }
    }
  };

  const handlePreviewImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const endpoint = importType === 'excel' 
        ? `${API}/accounts/import-excel/preview?organization_id=${currentOrg.id}`
        : `${API}/accounts/import-csv/preview?organization_id=${currentOrg.id}`;
      
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreviewData(response.data);
      setImportStep('preview');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to preview file');
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const endpoint = importType === 'excel'
        ? `${API}/accounts/import-excel?organization_id=${currentOrg.id}`
        : `${API}/accounts/import-csv?organization_id=${currentOrg.id}`;
      
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      setImportStep('result');
      fetchAccounts(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/accounts/template-csv`);
      const blob = new Blob([response.data.template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'accounts_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download template');
    }
  };

  const resetImportDialog = () => {
    setImportFile(null);
    setPreviewData(null);
    setImportResult(null);
    setImportStep('upload');
    setImportType('csv');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = account.code.includes(searchTerm) || 
                         account.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = selectedClass === 'all' || account.account_class === parseInt(selectedClass);
    return matchesSearch && matchesClass;
  });

  const groupedAccounts = filteredAccounts.reduce((groups, account) => {
    const classNum = account.account_class;
    if (!groups[classNum]) {
      groups[classNum] = [];
    }
    groups[classNum].push(account);
    return groups;
  }, {});

  // Filtered list for the advanced filter dialog
  const advancedFilteredAccounts = accounts.filter(account => {
    // Content filter (search in code, name, name_fr, name_pt)
    const matchesContent = !filterContent || 
      account.code.toLowerCase().includes(filterContent.toLowerCase()) ||
      (account.name || '').toLowerCase().includes(filterContent.toLowerCase()) ||
      (account.name_fr || '').toLowerCase().includes(filterContent.toLowerCase()) ||
      (account.name_pt || '').toLowerCase().includes(filterContent.toLowerCase());
    
    // Balance filter
    const hasBalance = (account.balance_usd !== 0) || (account.balance_lbp !== 0);
    let matchesBalance = true;
    if (filterBalance === 'non-zero') {
      matchesBalance = hasBalance;
    } else if (filterBalance === 'zero') {
      matchesBalance = !hasBalance;
    }
    
    // Code length filter
    const codeLen = account.code.length;
    let matchesCodeLength = true;
    if (filterCodeLength === '2') {
      matchesCodeLength = codeLen === 2 || codeLen === 1;
    } else if (filterCodeLength === '4') {
      matchesCodeLength = codeLen === 3 || codeLen === 4;
    } else if (filterCodeLength === 'more') {
      matchesCodeLength = codeLen > 4;
    }
    
    return matchesContent && matchesBalance && matchesCodeLength;
  }).sort((a, b) => a.code.localeCompare(b.code));

  // Print filtered list
  const printFilteredList = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Chart of Accounts - Filtered List</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
          h1 { font-size: 16px; margin-bottom: 5px; }
          .subtitle { color: #666; font-size: 11px; margin-bottom: 15px; }
          .filters { background: #f5f5f5; padding: 8px; margin-bottom: 15px; font-size: 10px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f0f0f0; font-weight: bold; }
          .number { text-align: right; font-family: monospace; }
          .code { font-family: monospace; font-weight: bold; }
          .negative { color: red; }
          .positive { color: green; }
          .footer { margin-top: 15px; font-size: 10px; color: #666; }
          @media print { body { margin: 10px; } }
        </style>
      </head>
      <body>
        <h1>Chart of Accounts - Filtered List</h1>
        <div class="subtitle">${currentOrg?.name || 'Organization'} - Printed on ${(() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`; })()}</div>
        <div class="filters">
          <strong>Filters Applied:</strong>
          ${filterContent ? `Content: "${filterContent}" | ` : ''}
          Balance: ${filterBalance === 'all' ? 'All' : filterBalance === 'non-zero' ? 'Non-Zero' : 'Zero'} | 
          Code Length: ${filterCodeLength === 'all' ? 'All' : filterCodeLength === '2' ? '1-2 digits' : filterCodeLength === '4' ? '3-4 digits' : '>4 digits'}
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 80px;">Code</th>
              <th>Account Name</th>
              <th>Name (FR)</th>
              <th style="width: 70px;">Class</th>
              <th style="width: 70px;">Type</th>
              <th style="width: 120px;" class="number">Balance (XOF)</th>
              <th style="width: 100px;" class="number">Balance (EUR)</th>
            </tr>
          </thead>
          <tbody>
            ${advancedFilteredAccounts.map(acc => `
              <tr>
                <td class="code">${acc.code}</td>
                <td>${acc.name}</td>
                <td>${acc.name_fr || '-'}</td>
                <td>Class ${acc.account_class}</td>
                <td style="text-transform: capitalize;">${acc.account_type}</td>
                <td class="number ${acc.balance_lbp < 0 ? 'negative' : acc.balance_lbp > 0 ? 'positive' : ''}">${Number(acc.balance_lbp || 0).toLocaleString('en-US')}</td>
                <td class="number ${acc.balance_usd < 0 ? 'negative' : acc.balance_usd > 0 ? 'positive' : ''}">€${Number(acc.balance_usd || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          Total Accounts: ${advancedFilteredAccounts.length} | 
          Total XOF: ${advancedFilteredAccounts.reduce((sum, a) => sum + (a.balance_lbp || 0), 0).toLocaleString('en-US')} | 
          Total EUR: €${advancedFilteredAccounts.reduce((sum, a) => sum + (a.balance_usd || 0), 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const resetFilterList = () => {
    setFilterContent('');
    setFilterBalance('all');
    setFilterCodeLength('all');
  };

  const accountTypeOptions = [
    { value: 'asset', label: 'Asset' },
    { value: 'liability', label: 'Liability' },
    { value: 'equity', label: 'Equity' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'expense', label: 'Expense' },
    { value: 'other', label: 'Other' },
    { value: 'analytical', label: 'Analytic' },
  ];

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="chart-of-accounts-page">
      {/* Failed Operation Banner - Re-update Button */}
      {failedOperation && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-400">
                Failed to {failedOperation.type === 'update' ? 'update' : 'create'} account
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Account: {failedOperation.payload?.code} - {failedOperation.payload?.name}
              </p>
              <p className="text-xs text-red-400/70 mt-0.5">
                Error: {failedOperation.error}
              </p>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setFailedOperation(null)}
              className="flex-1 sm:flex-initial text-xs"
            >
              <X className="w-3 h-3 mr-1" />
              Dismiss
            </Button>
            <Button 
              size="sm" 
              onClick={handleRetryOperation}
              disabled={retrying}
              className="flex-1 sm:flex-initial text-xs bg-red-500 hover:bg-red-600"
              data-testid="retry-account-btn"
            >
              {retrying ? (
                <>
                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Re-update Account
                </>
              )}
            </Button>
          </div>
        </div>
      )}
      
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Chart of Accounts
          </h1>
          <p className="text-muted-foreground text-xs lg:text-sm mt-1">
            Chart of Accounts (OHADA / SYSCOHADA) - Classes 1-9
          </p>
        </div>
        
        {/* Action buttons - responsive grid */}
        <div className="flex flex-wrap gap-2">
          {canAdmin() && accounts.length === 0 && (
            <Dialog open={seedDialogOpen} onOpenChange={setSeedDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs" data-testid="seed-coa-btn">
                  <Download className="w-3 h-3 mr-1 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden sm:inline">Seed Default</span>
                  <span className="sm:hidden">Seed</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Seed Default Chart of Accounts</DialogTitle>
                  <DialogDescription>
                    This will create the full multilingual (English / French / Portuguese) OHADA/SYSCOHADA chart of accounts (~584 accounts across Classes 1-7). If this organization already has accounts, they will be replaced.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm text-muted-foreground">Includes Classes 1-7:</p>
                  <ul className="text-xs sm:text-sm mt-2 space-y-1 text-muted-foreground">
                    <li>• Capital, Fixed Assets, Inventory</li>
                    <li>• Third Party, Financial Accounts</li>
                    <li>• Expenses, Revenue</li>
                  </ul>
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setSeedDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                  <Button onClick={handleSeedCOA} disabled={seeding} className="w-full sm:w-auto">
                    {seeding ? 'Seeding...' : 'Seed Accounts'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          
          {/* CSV/Excel Import Button */}
          {canAdmin() && (
            <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) resetImportDialog(); }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs" data-testid="import-csv-btn">
                  <Upload className="w-3 h-3 mr-1 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden sm:inline">Import</span>
                  <span className="sm:hidden">Import</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Import Accounts from CSV/Excel</DialogTitle>
                  <DialogDescription>
                    Upload a CSV or Excel (.xlsx) file to import accounts. Supports OHADA/SYSCOHADA format.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Step 1: Upload */}
                  {importStep === 'upload' && (
                    <>
                      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                        <span className="text-sm">Need a CSV template?</span>
                        <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                          <FileDown className="w-4 h-4 mr-2" />
                          Download Template
                        </Button>
                      </div>
                      
                      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="csv-upload"
                        />
                        <label htmlFor="csv-upload" className="cursor-pointer">
                          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                          <p className="text-sm font-medium">Click to select CSV or Excel file</p>
                          <p className="text-xs text-muted-foreground mt-1">Supports .csv, .xlsx, .xls</p>
                        </label>
                      </div>
                      
                      {importFile && (
                        <div className="flex items-center justify-between p-3 bg-primary/10 rounded-sm">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span className="text-sm font-medium">{importFile.name}</span>
                            <span className="text-xs px-2 py-0.5 bg-primary/20 rounded">
                              {importType === 'excel' ? 'Excel' : 'CSV'}
                            </span>
                          </div>
                          <Button size="sm" onClick={handlePreviewImport} disabled={importing}>
                            {importing ? 'Loading...' : 'Preview Import'}
                          </Button>
                        </div>
                      )}
                      
                      <div className="text-xs text-muted-foreground space-y-2">
                        <p><strong>CSV Format:</strong> acc_code / code, account name(en), account name(Fr), account name(Pr) — or name, name_fr, name_pt, account_type</p>
                        <p><strong>Excel Format:</strong> acc_code, account name(en), account name(Fr), account name(Pr). Type, class &amp; parent are auto-derived from the code.</p>
                      </div>
                    </>
                  )}
                  
                  {/* Step 2: Preview */}
                  {importStep === 'preview' && previewData && (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                        <div className="p-3 bg-muted/30 rounded-sm">
                          <p className="text-lg font-bold">{previewData.total_rows}</p>
                          <p className="text-xs text-muted-foreground">Total Rows</p>
                        </div>
                        <div className="p-3 bg-emerald-500/20 rounded-sm">
                          <p className="text-lg font-bold text-emerald-400">{previewData.new_accounts}</p>
                          <p className="text-xs text-muted-foreground">New</p>
                        </div>
                        <div className="p-3 bg-blue-500/20 rounded-sm">
                          <p className="text-lg font-bold text-blue-400">{previewData.update_accounts}</p>
                          <p className="text-xs text-muted-foreground">Updates</p>
                        </div>
                        <div className="p-3 bg-red-500/20 rounded-sm">
                          <p className="text-lg font-bold text-red-400">{previewData.invalid_rows}</p>
                          <p className="text-xs text-muted-foreground">Errors</p>
                        </div>
                      </div>
                      
                      {previewData.errors.length > 0 && (
                        <div className="p-3 bg-red-500/10 rounded-sm">
                          <p className="text-sm font-medium text-red-400 mb-2">Errors found:</p>
                          <ul className="text-xs text-red-400 space-y-1 max-h-24 overflow-y-auto">
                            {previewData.errors.map((err, i) => (
                              <li key={i}>• {err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {previewData.preview_data.length > 0 && (
                        <div className="overflow-x-auto max-h-48">
                          <table className="w-full text-xs">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="px-2 py-1 text-left">Action</th>
                                <th className="px-2 py-1 text-left">Code</th>
                                <th className="px-2 py-1 text-left">Name (EN)</th>
                                <th className="px-2 py-1 text-left">Name (FR)</th>
                                <th className="px-2 py-1 text-left">Name (PT)</th>
                                <th className="px-2 py-1 text-left">Parent</th>
                                <th className="px-2 py-1 text-left">Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {previewData.preview_data.slice(0, 10).map((row, i) => (
                                <tr key={i} className="border-t border-border">
                                  <td className="px-2 py-1">
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                      row.action === 'create' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      {row.action}
                                    </span>
                                  </td>
                                  <td className="px-2 py-1 font-mono">{row.code}</td>
                                  <td className="px-2 py-1">{row.name}</td>
                                  <td className="px-2 py-1 text-muted-foreground">{row.name_fr || '-'}</td>
                                  <td className="px-2 py-1 text-muted-foreground">{row.name_pt || '-'}</td>
                                  <td className="px-2 py-1 font-mono text-muted-foreground">{row.parent_code || '-'}</td>
                                  <td className="px-2 py-1">{row.account_type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {previewData.preview_data.length > 10 && (
                            <p className="text-xs text-muted-foreground text-center py-2">
                              ... and {previewData.preview_data.length - 10} more rows
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={resetImportDialog} className="flex-1">
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleConfirmImport} 
                          disabled={importing || previewData.valid_rows === 0}
                          className="flex-1"
                        >
                          {importing ? 'Importing...' : `Import ${previewData.valid_rows} Accounts`}
                        </Button>
                      </div>
                    </>
                  )}
                  
                  {/* Step 3: Result */}
                  {importStep === 'result' && importResult && (
                    <div className="text-center space-y-4">
                      <div className={`p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center ${
                        importResult.success ? 'bg-emerald-500/20' : 'bg-red-500/20'
                      }`}>
                        {importResult.success ? (
                          <Check className="w-8 h-8 text-emerald-400" />
                        ) : (
                          <X className="w-8 h-8 text-red-400" />
                        )}
                      </div>
                      <p className="text-lg font-medium">{importResult.message}</p>
                      <div className="flex justify-center gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-400">{importResult.created}</p>
                          <p className="text-xs text-muted-foreground">Created</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-400">{importResult.updated}</p>
                          <p className="text-xs text-muted-foreground">Updated</p>
                        </div>
                      </div>
                      {importResult.errors.length > 0 && (
                        <div className="p-3 bg-red-500/10 rounded-sm text-left">
                          <p className="text-sm font-medium text-red-400 mb-2">Some errors occurred:</p>
                          <ul className="text-xs text-red-400 space-y-1">
                            {importResult.errors.map((err, i) => (
                              <li key={i}>• {err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <Button onClick={() => setImportDialogOpen(false)} className="w-full">
                        Done
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Rebuild from Vouchers Button */}
          {canAdmin() && (
            <Button 
              variant="outline" 
              size="sm" 
              className="text-xs"
              onClick={handleRebuildFromVouchers}
              disabled={rebuildingFromVouchers}
              data-testid="rebuild-from-vouchers-btn"
            >
              {rebuildingFromVouchers ? (
                <>
                  <RefreshCw className="w-3 h-3 mr-1 lg:w-4 lg:h-4 lg:mr-2 animate-spin" />
                  <span className="hidden sm:inline">Rebuilding...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3 mr-1 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden sm:inline">Rebuild from Vouchers</span>
                  <span className="sm:hidden">Rebuild</span>
                </>
              )}
            </Button>
          )}
          
          {/* Filtered List Button */}
          <Dialog open={filterListOpen} onOpenChange={setFilterListOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs" data-testid="filtered-list-btn">
                <Filter className="w-3 h-3 mr-1 lg:w-4 lg:h-4 lg:mr-2" />
                <span className="hidden sm:inline">Filtered List</span>
                <span className="sm:hidden">Filter</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Advanced Filter & Print</DialogTitle>
                <DialogDescription>
                  Filter accounts by content, balance, and code length. Print or view the filtered list.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="filter-content" className="text-sm">Search Content</Label>
                    <Input
                      id="filter-content"
                      placeholder="Code or name..."
                      value={filterContent}
                      onChange={(e) => setFilterContent(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="filter-balance" className="text-sm">Balance Filter</Label>
                    <Select value={filterBalance} onValueChange={setFilterBalance}>
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Balances</SelectItem>
                        <SelectItem value="non-zero">Non-Zero Balance</SelectItem>
                        <SelectItem value="zero">Zero Balance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="filter-code-length" className="text-sm">Code Length</Label>
                    <Select value={filterCodeLength} onValueChange={setFilterCodeLength}>
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Lengths</SelectItem>
                        <SelectItem value="2">1-2 Digits</SelectItem>
                        <SelectItem value="4">3-4 Digits</SelectItem>
                        <SelectItem value="more">&gt;4 Digits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

          {/* Bulk Delete Selection Bar */}
          {canAdmin() && selectedAccounts.size > 0 && (
            <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-background border border-border shadow-lg rounded-lg px-4 py-3 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{selectedAccounts.size} selected</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedAccounts(new Set())}
              >
                Clear
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => setBulkDeleteDialogOpen(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Selected
              </Button>
            </div>
          )}

          {/* Bulk Delete Dialog */}
          <Dialog open={bulkDeleteDialogOpen} onOpenChange={closeBulkDeleteDialog}>
            <DialogContent className="max-w-[95vw] sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  Confirm Bulk Delete
                </DialogTitle>
                <DialogDescription>
                  You are about to delete {selectedAccounts.size} account(s). Only accounts with no transactions will be deleted.
                </DialogDescription>
              </DialogHeader>
              
              {!bulkDeleteResult ? (
                <div className="space-y-4 py-4">
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      <strong>Safety check:</strong> Accounts with balance or transaction history will be automatically skipped.
                    </p>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Code</th>
                          <th className="px-2 py-1 text-left">Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.filter(a => selectedAccounts.has(a.id)).map(acc => (
                          <tr key={acc.id} className="border-t border-border">
                            <td className="px-2 py-1 font-mono">{acc.code}</td>
                            <td className="px-2 py-1 truncate max-w-[200px]">{acc.name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={closeBulkDeleteDialog}>
                      Cancel
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleBulkDelete}
                      disabled={bulkDeleting}
                    >
                      {bulkDeleting ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          Confirm Delete
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <p className="text-2xl font-bold text-green-500">{bulkDeleteResult.deleted_count}</p>
                      <p className="text-xs text-muted-foreground">Deleted</p>
                    </div>
                    <div className="p-3 bg-yellow-500/10 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-500">{bulkDeleteResult.skipped_count}</p>
                      <p className="text-xs text-muted-foreground">Skipped</p>
                    </div>
                  </div>
                  
                  {bulkDeleteResult.skipped?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Skipped accounts (have transactions or children):</p>
                      <div className="max-h-40 overflow-y-auto border rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left">Code</th>
                              <th className="px-2 py-1 text-left">Name</th>
                              <th className="px-2 py-1 text-left">Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkDeleteResult.skipped.map((item, i) => (
                              <tr key={i} className="border-t border-border">
                                <td className="px-2 py-1 font-mono">{item.code}</td>
                                <td className="px-2 py-1 truncate max-w-[120px]">{item.name}</td>
                                <td className="px-2 py-1 text-yellow-600 truncate max-w-[150px]">{item.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  <Button onClick={closeBulkDeleteDialog} className="w-full">
                    Done
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
                </div>
                
                <div className="border rounded-lg p-3 bg-muted/30">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium">Filtered Results</h4>
                    <span className="text-xs text-muted-foreground">
                      {advancedFilteredAccounts.length} accounts
                    </span>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto">
                    {advancedFilteredAccounts.length > 0 ? (
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Code</th>
                            <th className="px-2 py-1 text-left">Name</th>
                            <th className="px-2 py-1 text-left">Class</th>
                            <th className="px-2 py-1 text-right">XOF</th>
                            <th className="px-2 py-1 text-right">EUR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {advancedFilteredAccounts.slice(0, 50).map((acc) => (
                            <tr key={acc.id} className="border-t border-border">
                              <td className="px-2 py-1 font-mono">{acc.code}</td>
                              <td className="px-2 py-1 truncate max-w-32">{acc.name}</td>
                              <td className="px-2 py-1">{acc.account_class}</td>
                              <td className="px-2 py-1 text-right font-mono">
                                {Number(acc.balance_lbp || 0).toLocaleString()}
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                €{Number(acc.balance_usd || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No accounts match the current filters
                      </p>
                    )}
                    {advancedFilteredAccounts.length > 50 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        ... and {advancedFilteredAccounts.length - 50} more accounts
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={resetFilterList} className="flex-1">
                    Reset Filters
                  </Button>
                  <Button 
                    onClick={printFilteredList} 
                    disabled={advancedFilteredAccounts.length === 0}
                    className="flex-1"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print List ({advancedFilteredAccounts.length})
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {canEdit() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="btn-glow text-xs" size="sm" data-testid="add-account-btn">
                  <Plus className="w-3 h-3 mr-1 lg:w-4 lg:h-4 lg:mr-2" />
                  <span className="hidden sm:inline">Add Account</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {editingAccount ? 'Edit Account' : 'Add New Account'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateAccount} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="code" className="text-xs sm:text-sm">Account Code</Label>
                      <Input
                        id="code"
                        placeholder="e.g., 51201"
                        value={newAccount.code}
                        onChange={(e) => setNewAccount({ ...newAccount, code: e.target.value })}
                        required
                        className="font-mono text-sm"
                        data-testid="account-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class" className="text-xs sm:text-sm">Class</Label>
                      <Select
                        value={String(newAccount.account_class)}
                        onValueChange={(value) => setNewAccount({ ...newAccount, account_class: parseInt(value) })}
                      >
                        <SelectTrigger data-testid="account-class-select" className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                            <SelectItem key={num} value={String(num)}>
                              Class {num}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs sm:text-sm">Account Name (English)</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Bank - Main Account"
                      value={newAccount.name}
                      onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                      required
                      className="text-sm"
                      data-testid="account-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="name_fr" className="text-xs sm:text-sm">Account Name (Français)</Label>
                      <Input
                        id="name_fr"
                        placeholder="ex. Banque - Compte Principal"
                        value={newAccount.name_fr}
                        onChange={(e) => setNewAccount({ ...newAccount, name_fr: e.target.value })}
                        className="text-sm"
                        data-testid="account-name-fr-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="name_pt" className="text-xs sm:text-sm">Account Name (Português)</Label>
                      <Input
                        id="name_pt"
                        placeholder="ex. Banco - Conta Principal"
                        value={newAccount.name_pt}
                        onChange={(e) => setNewAccount({ ...newAccount, name_pt: e.target.value })}
                        className="text-sm"
                        data-testid="account-name-pt-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="type" className="text-xs sm:text-sm">Account Type</Label>
                    <Select
                      value={newAccount.account_type}
                      onValueChange={(value) => setNewAccount({ ...newAccount, account_type: value })}
                    >
                      <SelectTrigger data-testid="account-type-select" className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {accountTypeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="normal_balance" className="text-xs sm:text-sm">Normal Balance</Label>
                      <Select
                        value={newAccount.normal_balance || 'debit'}
                        onValueChange={(value) => setNewAccount({ ...newAccount, normal_balance: value })}
                      >
                        <SelectTrigger data-testid="normal-balance-select" className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="debit">Debit</SelectItem>
                          <SelectItem value="credit">Credit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="is_group" className="text-xs sm:text-sm">Account Level</Label>
                      <Select
                        value={newAccount.is_group ? 'group' : 'postable'}
                        onValueChange={(value) => setNewAccount({ ...newAccount, is_group: value === 'group' })}
                      >
                        <SelectTrigger data-testid="is-group-select" className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="postable">Postable (detail)</SelectItem>
                          <SelectItem value="group">Group / Header</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="w-full sm:w-auto">
                      Cancel
                    </Button>
                    <Button type="submit" className="w-full sm:w-auto" data-testid="save-account-btn">
                      {editingAccount ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by code or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 text-sm h-9"
                data-testid="account-search"
              />
            </div>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-full sm:w-[140px] text-sm h-9" data-testid="class-filter">
                <SelectValue placeholder="Filter class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                  <SelectItem key={num} value={String(num)}>Class {num}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedAccounts).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([classNum, classAccounts]) => (
            <Card key={classNum} className={`border-l-4 border-l-${['violet', 'blue', 'cyan', 'amber', 'emerald', 'red', 'green', 'violet', 'cyan'][parseInt(classNum) - 1]}-400`} data-testid={`account-class-${classNum}`}>
              <CardHeader className="py-2 px-3 lg:py-3 lg:px-4">
                <CardTitle className="text-xs lg:text-sm flex items-center gap-1 lg:gap-2">
                  <span className="text-muted-foreground">Class {classNum}</span>
                  <ChevronRight className="w-3 h-3 text-muted-foreground" />
                  <span className="truncate">{getAccountClassName(parseInt(classNum))}</span>
                  <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{classAccounts.length}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Mobile view */}
                <div className="lg:hidden">
                  {classAccounts.map((account, idx) => {
                    const isZeroBalance = hasZeroBalance(account);
                    const isSelected = selectedAccounts.has(account.id);
                    const isHiddenFromCashier = account.hidden_from_cashier;
                    return (
                      <div key={account.id} className={`p-3 border-b border-border ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'} ${isSelected ? 'bg-primary/10' : ''} ${isHiddenFromCashier && user?.role === 'super_admin' ? 'opacity-60' : ''}`} data-testid={`account-row-${account.code}`}>
                        <div className="flex items-start justify-between gap-2">
                          {canAdmin() && (
                            <div className="flex-shrink-0 pt-1">
                              {isZeroBalance ? (
                                <button
                                  onClick={() => toggleAccountSelection(account.id)}
                                  className="p-1 hover:bg-muted rounded"
                                >
                                  {isSelected ? (
                                    <CheckSquare className="w-4 h-4 text-primary" />
                                  ) : (
                                    <Square className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </button>
                              ) : (
                                <span className="w-6 h-6 inline-block" />
                              )}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-sm">{account.code}</span>
                              <span className="text-xs text-muted-foreground capitalize">{account.account_type}</span>
                              {isHiddenFromCashier && user?.role === 'super_admin' && (
                                <span className="text-xs text-orange-400 bg-orange-100 dark:bg-orange-900/30 px-1.5 py-0.5 rounded">Hidden</span>
                              )}
                            </div>
                            <p className="text-sm truncate">{accName(account)}</p>
                            {lang !== 'en' && account.name && <p className="text-xs text-muted-foreground truncate">{account.name}</p>}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setLedgerAccount(account)} title="View Ledger">
                              <List className="w-3 h-3" />
                            </Button>
                            {/* Toggle cashier visibility - super_admin only */}
                            {user?.role === 'super_admin' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className={`h-7 w-7 p-0 ${account.hidden_from_cashier ? "text-orange-400" : "text-muted-foreground"}`}
                                onClick={() => handleToggleCashierVisibility(account)} 
                                title={account.hidden_from_cashier ? "Hidden from cashier" : "Visible to cashier"}
                              >
                                {account.hidden_from_cashier ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </Button>
                            )}
                            {canEdit() && (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditAccount(account)} title="Edit">
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                {canAdmin() && (
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => setDeleteConfirm(account)} title="Delete">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-3 mt-2 text-xs">
                          <span className={getNumberClass(account.balance_lbp)}>XOF: {formatLBP(account.balance_lbp)}</span>
                          <span className={getNumberClass(account.balance_usd)}>EUR: €{formatUSD(account.balance_usd)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop table */}
                <table className="hidden lg:table data-table">
                  <thead>
                    <tr>
                      {canAdmin() && (
                        <th className="w-10 text-center">
                          <button
                            onClick={toggleSelectAll}
                            className="p-1 hover:bg-muted rounded"
                            title={getSelectAllState() === 'all' ? 'Deselect all' : 'Select all (zero balance only)'}
                          >
                            {getSelectAllState() === 'all' ? (
                              <CheckSquare className="w-4 h-4 text-primary" />
                            ) : getSelectAllState() === 'partial' ? (
                              <MinusSquare className="w-4 h-4 text-primary" />
                            ) : (
                              <Square className="w-4 h-4 text-muted-foreground" />
                            )}
                          </button>
                        </th>
                      )}
                      <th className="w-24">Code</th>
                      <th>Account Name</th>
                      <th>Translation</th>
                      <th>Type</th>
                      <th className="text-right">Balance (XOF)</th>
                      <th className="text-right">Balance (EUR)</th>
                      <th className="w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {classAccounts.map((account, idx) => {
                      const isZeroBalance = hasZeroBalance(account);
                      const isSelected = selectedAccounts.has(account.id);
                      const isHiddenFromCashier = account.hidden_from_cashier;
                      return (
                        <tr key={account.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'} ${isSelected ? 'bg-primary/10' : ''} ${isHiddenFromCashier && user?.role === 'super_admin' ? 'opacity-60' : ''} hover:bg-blue-50/50 transition-colors`} data-testid={`account-row-${account.code}`}>
                          {canAdmin() && (
                            <td className="text-center">
                              {isZeroBalance ? (
                                <button
                                  onClick={() => toggleAccountSelection(account.id)}
                                  className="p-1 hover:bg-muted rounded"
                                  title={isSelected ? 'Deselect' : 'Select for deletion'}
                                >
                                  {isSelected ? (
                                    <CheckSquare className="w-4 h-4 text-primary" />
                                  ) : (
                                    <Square className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground" title="Has balance - cannot select">—</span>
                              )}
                            </td>
                          )}
                          <td className="font-mono font-medium">
                            {account.code}
                            {isHiddenFromCashier && user?.role === 'super_admin' && (
                              <span className="ml-2 text-xs text-orange-400 bg-orange-100 dark:bg-orange-900/30 px-1.5 py-0.5 rounded">Hidden</span>
                            )}
                          </td>
                          <td>{accName(account)}</td>
                          <td className="text-muted-foreground">{lang !== 'en' ? (account.name || '-') : (account.name_fr || '-')}</td>
                          <td><span className="text-xs text-muted-foreground capitalize">{account.account_type}</span></td>
                          <td className={`number ${getNumberClass(account.balance_lbp)}`}>{formatLBP(account.balance_lbp)}</td>
                          <td className={`number ${getNumberClass(account.balance_usd)}`}>€{formatUSD(account.balance_usd)}</td>
                          <td>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => setLedgerAccount(account)} title="View Ledger">
                                <List className="w-3 h-3" />
                              </Button>
                              {/* Toggle cashier visibility - super_admin only */}
                              {user?.role === 'super_admin' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleToggleCashierVisibility(account)} 
                                  title={account.hidden_from_cashier ? "Hidden from cashier - click to show" : "Visible to cashier - click to hide"}
                                  className={account.hidden_from_cashier ? "text-orange-400" : "text-muted-foreground"}
                                >
                                  {account.hidden_from_cashier ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                </Button>
                              )}
                              {canEdit() && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => handleEditAccount(account)} title="Edit">
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  {canAdmin() && (
                                    <Button variant="ghost" size="sm" className="text-red-400" onClick={() => setDeleteConfirm(account)} title="Delete">
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}

          {filteredAccounts.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No accounts found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm ? 'Try adjusting your search' : accounts.length === 0 ? 'Seed default accounts, import CSV, or add your first account' : 'Add your first account to get started'}
              </p>
            </div>
          )}
          
          {/* Load More */}
          {hasMore && filteredAccounts.length > 0 && (
            <div className="text-center py-4">
              <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : `Load More (${accounts.length} of ${totalCount})`}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete account <strong>{deleteConfirm?.code} - {deleteConfirm?.name}</strong>?
              {(deleteConfirm?.balance_lbp !== 0 || deleteConfirm?.balance_usd !== 0) && (
                <span className="block mt-2 text-red-400">
                  Warning: This account has a non-zero balance and cannot be deleted.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="w-full sm:w-auto">Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={deleteConfirm?.balance_lbp !== 0 || deleteConfirm?.balance_usd !== 0}
              className="w-full sm:w-auto"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ledger Dialog */}
      <LedgerDialog
        account={ledgerAccount}
        organizationId={currentOrg?.id}
        open={!!ledgerAccount}
        onClose={() => setLedgerAccount(null)}
        userRole={user?.role}
        fyId={selectedFY?.id}
      />
    </div>
  );
};

export default ChartOfAccountsPage;

